import type { NavigationGuardReturn, RouteLocationNormalized, RouteParams } from "vue-router";

import { checkoutRoutes } from "~/app/router.options";
import { Language, Page, Pages } from "~/utils/constants";
import { stringToSlug } from "~/utils/parsers/url";

/**
 * Redirect the user to the translated page if the current page is not translated.
 */
export default defineNuxtRouteMiddleware(middleware);

export async function middleware(to: RouteLocationNormalized): Promise<NavigationGuardReturn> {
  if (to.path.startsWith(Page.ADMIN) || to.path.startsWith(Page.AUTH)) {
    return;
  }

  const localizationMiddleware = new LocalizationMiddleware(to);
  return localizationMiddleware.handleLocalization();
}

interface PageInfo {
  path: string;
  params: string[];
}

type PageName = keyof (typeof Pages)[Language.EN | Language.FR];

class LocalizationMiddleware {
  private readonly currentPath: string;
  private readonly currentParams: RouteParams;
  private readonly language: Language;

  constructor(to: RouteLocationNormalized) {
    this.currentPath = to.path;
    this.currentParams = to.params;
    this.language = this.retrieveLanguage();
  }

  /**
   * Handle the localization based on the current page and language.
   *
   * This is going to redirect the user to the correct translated page if the
   * current page is not translated or if it's incorrectly translated.
   */
  public async handleLocalization(): Promise<ReturnType<typeof navigateTo> | undefined> {
    const matchingPage = this.getMatchingPage();

    if (matchingPage) {
      // If the current page is already a translated page, we don't need to redirect
      const currentPathIsTranslated = this.isCurrentPathTranslated(matchingPage);
      if (currentPathIsTranslated) {
        return;
      }

      // If the current page is not correctly translated and has all necessary
      // parameters, redirect to the translated page
      const pageHasAllNecessaryParams = this.hasAllNecessaryParams(matchingPage.params);
      if (pageHasAllNecessaryParams) {
        const translatedPage = this.getFullPath(matchingPage);
        return navigateTo(translatedPage);
      }
    }

    // If the current page is not a valid checkout page, redirect to the fallback page
    const fallbackPage = this.getFallbackPage();
    return navigateTo(fallbackPage);
  }

  /**
   * Retrieve the current language based on the URL path or parameters.
   *
   * This function handles the edge case where the user navigates to a page
   * with multiple language codes in the URL path, such as "/fr/en/checkout/search".
   * In this scenario, the path is not valid and not matched with any route,
   * resulting in the language parameter missing from the parameters.
   */
  private retrieveLanguage() {
    const languageParam = (this.currentParams?.language ?? null) as Language | null;
    let language: Language | null = languageParam;

    if (!language) {
      const languageFromPath = (this.currentPath.split("/")?.[1] ?? "") as Language;

      if ([Language.EN, Language.FR].includes(languageFromPath)) {
        language = languageFromPath;
      }
    }

    return language ?? Language.EN;
  }

  /**
   * Get the valid translated page based on the current language and
   * parameters.
   *
   * Take a look on the following example.
   * const checkoutRoutes = [
   *   { name: "search", path: "/search" },
   *   { name: "details", path: "/details/123" },
   *  ];
   *
   * return {
   *   path: "en/checkout/details",
   *   params: ["123"]
   * }
   */
  private getMatchingPage(): PageInfo | null {
    const currentRoute = checkoutRoutes.find((route) => {
      const routeName = route.name as PageName;

      return (
        this.currentPath.includes(Pages[Language.EN][routeName]) ||
        this.currentPath.includes(Pages[Language.FR][routeName])
      );
    });

    if (!currentRoute || !this.isValidRoute(currentRoute)) {
      return null;
    }

    return {
      path: this.constructTranslatedPath(currentRoute.name as PageName),
      params: this.extractParamsFromRoutePath(currentRoute.path),
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private isValidRoute(route: any): route is { name: string; path: string } {
    return typeof route.name === "string" && typeof route.path === "string";
  }

  /**
   * Construct the translated path based on the current language.
   *
   * NOTE: It prepends all pages with the language code and the "checkout"
   * translated keyword.
   */
  private constructTranslatedPath(pageName: PageName): string {
    const { checkout } = Pages[this.language];
    const translatedPageName = Pages[this.language][pageName];

    return stringToSlug(`/${this.language}/${checkout}/${translatedPageName}`);
  }

  /**
   * Extract the parameters from a route path.
   *
   * For example, if the route path is "/details/:inventorySlug" and the 'currentParams'
   * object contains { inventorySlug: "123" }, the method will return ["123"].
   *
   * Parameters:
   * - path: The route path containing dynamic parameter placeholders (e.g., ":inventorySlug").
   *
   * Note:
   * The method does not filter out undefined values because it is assumed that
   * the presence of all necessary parameters in the 'currentParams' object is later validated.
   */
  private extractParamsFromRoutePath(path: string): string[] {
    return (
      path
        .split("/")
        // Remove the first element because it's a dynamic page name
        .slice(1)
        // Get only the dynamic parameters from the path
        .filter((param) => param.startsWith(":"))
        .map((param) => {
          // Remove the ":" and the optional "?" from the parameter
          const paramName = param.slice(1).replace(/\?$/, "");
          return this.currentParams[paramName] as string;
        })
    );
  }

  /**
   * Check if the current path is already a translated page.
   */
  private isCurrentPathTranslated(matchingPage: PageInfo): boolean {
    return this.currentPath === this.getFullPath(matchingPage);
  }

  /**
   * Check if all necessary parameters are present in the current path.
   */
  private hasAllNecessaryParams(params: string[] = []): boolean {
    return params.every(Boolean);
  }

  /**
   * Construct the full path with the given parameters.
   */
  private getFullPath({ path, params }: PageInfo): string {
    if (!params || !params.length) {
      return path;
    }

    return `${path}/${params.join("/")}`;
  }

  private getFallbackPage(): string {
    const { checkout, search } = Pages[this.language];
    return `/${this.language}/${checkout}/${search}`;
  }
}
